import React from 'react';
import { Container, Typography, Button } from '@mui/material';
import {
  Instagram,
  Facebook,
  Twitter,
  YouTube,
  LinkedIn,
} from '@mui/icons-material';

import { useIntl } from '@klokhuis/core/Intl';
import { NavLink } from '@klokhuis/core/Routing/NavLink';

import PropertyLinksGrid from './components/PropertyLinksGrid';
import styles from './Footer.module.css';
import { ROUTES } from '@klokhuis/core/Routing';
import { LogoLink } from '../Header/LogoLink';

export interface FooterProps {
  showPropertyLinksGrid: boolean;
}

export const Footer = ({ showPropertyLinksGrid }: FooterProps) => {
  const { T } = useIntl();

  return (
    <footer id="page-footer" data-testid="PageFooter" className={styles.root}>
      <div>
        {showPropertyLinksGrid && <PropertyLinksGrid />}
        <div className={styles.outerContainer}>
          <Container maxWidth="xl">
            <div className={styles.content}>
              {/* Brand section */}
              <div className={styles.column}>
                <LogoLink />
                <Typography
                  variant="subtitle1"
                  className={styles.brandDescription}
                >
                  {T('footer.brand_description')}
                </Typography>
              </div>

              {/* Menu section */}
              <div className={styles.column}>
                <Typography variant="h5" className={styles.columnTitle}>
                  {T('footer.menu.title')}
                </Typography>
                <div className={styles.linksList}>
                  <NavLink.Anchor
                    href={ROUTES.AboutUs.url({})}
                    className={styles.footerLink}
                  >
                    <Typography variant="body2">
                      {T('footer.menu.about')}
                    </Typography>
                  </NavLink.Anchor>

                  <NavLink.Anchor
                    href={ROUTES.PrivacyPolicy.url({})}
                    className={styles.footerLink}
                  >
                    <Typography variant="body2">
                      {T('footer.links.privacy_policy')}
                    </Typography>
                  </NavLink.Anchor>
                  <NavLink.Anchor
                    href={ROUTES.TermsAndConditions.url({})}
                    className={styles.footerLink}
                  >
                    <Typography variant="body2">
                      {T('footer.menu.terms_of_use')}
                    </Typography>
                  </NavLink.Anchor>
                </div>
              </div>

              {/* Links section */}
              <div className={styles.column}>
                <Typography variant="h5" className={styles.columnTitle}>
                  {T('footer.links.title')}
                </Typography>
                <div className={styles.linksList}>
                  <NavLink.Anchor
                    href={ROUTES.ContactUs.url({})}
                    className={styles.footerLink}
                  >
                    <Typography variant="body2">
                      {T('footer.links.contact_us')}
                    </Typography>
                  </NavLink.Anchor>
                  <NavLink.Anchor href={ROUTES.AgentsKnowledgeHub.url({})} className={styles.footerLink}>
                    <Typography variant="body2">
                      {T('footer.links.host_knowledge_hub')}
                    </Typography>
                  </NavLink.Anchor>
                   <NavLink.Anchor href={ROUTES.RealEstateInsights.url({})} className={styles.footerLink}>
                    <Typography variant="body2">
                      {T('footer.links.real_estate_insights')}
                    </Typography>
                  </NavLink.Anchor>
                  <NavLink.Anchor href="/sitemap.xml" className={styles.footerLink}>
                    <Typography variant="body2">
                      {T('footer.links.sitemap')}
                    </Typography>
                  </NavLink.Anchor>
                </div>
              </div>

              {/* Help section */}
              <div className={styles.column}>
                <Typography variant="h5" className={styles.columnTitle}>
                  {T('footer.help.title')}
                </Typography>
                <div className={styles.linksList}>
                  <NavLink.Anchor
                    href={ROUTES.HelpCenter.url({})}
                    className={styles.footerLink}
                  >
                    <Typography variant="body2">
                      {T('footer.help.help_center')}
                    </Typography>
                  </NavLink.Anchor>
                </div>
              </div>

              {/* Social section */}
              <div className={styles.socialContainer}>
                <Typography variant="h5" className={styles.columnTitle}>
                  {T('footer.social_links.title')}
                </Typography>
                <div className={styles.socialIcons}>
                  <Button
                    component="a"
                    href="https://www.instagram.com/findmyprops/"
                    target='_blank'
                    className={styles.socialIconBtn}
                  >
                    <Instagram />
                  </Button>
                  <Button
                    component="a"
                    href="#"
                    className={styles.socialIconBtn}
                  >
                    <Facebook />
                  </Button>
                  <Button
                    component="a"
                    href="#"
                    className={styles.socialIconBtn}
                  >
                    <Twitter />
                  </Button>
                  <Button
                    component="a"
                    href="#"
                    className={styles.socialIconBtn}
                  >
                    <YouTube />
                  </Button>
                  <Button
                    component="a"
                    href="https://www.linkedin.com/in/pallavi-r-943b2a35b/"
                    target='_blank'
                    className={styles.socialIconBtn}
                  >
                    <LinkedIn />
                  </Button>
                </div>
                <Typography variant="body1" className={styles.footerLink}>
                  {T('footer.copyright')}
                </Typography>
              </div>
            </div>
          </Container>
        </div>
      </div>
    </footer>
  );
};
